/**
 * Health ▸ Lookup's file — one Excel workbook and one CSV of the spot check (`docs/adr/3004`; ADR 9010,
 * 9045, 9072).
 *
 * One row per requested pair, in the order pasted: the store and the article with their names, the stock
 * state, on hand and in transit on the as-of day, and sold, returned and net value over the sales window.
 * With «by size» each pair row is followed by one row per size (`row_level` = `size`), carrying the size's
 * on hand, in transit and sold; returned and net value are the pair's only, blank on a size row. On hand is
 * the counted figure (negative positions as zero, ruling of 2026-10-03); the negative positions leave beside it
 * in `negative_on_hand_units`, blank where there are none.
 *
 * An absent figure leaves as absent, never 0: a pair with no row on a landed day is `not-ranged` and reads
 * «no row»; a day not landed, or a figure the source did not report, is `not-reported`. A pair the masters
 * do not know keeps its row, with its name absent.
 */

import { callOperation } from '@/api';
import { postLookup, type LookupResponseModel, type LookupRowModel, type LookupSizeModel } from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import type { ExportCell, ExportColumn, ExportRequest, ProvenanceFilter } from '@/export';
import type { ExportScope } from '../../manifest';

import { lookupBody, negativeOnHand, parsePairs, readLookupScope, storedPairs, type OnHandEntry } from './lookup';

const PLATFORM_VERSION = buildVersion();

export const LOOKUP_EXPORT_ID = 'strategic.healthLookup.pairs';

const notReported: ExportCell = { kind: 'missing', state: 'not-reported' };
const notApplicable: ExportCell = { kind: 'missing', state: 'not-ranged' };
const text = (value: string | null | undefined): ExportCell => (value === null || value === undefined || value === '' ? notReported : { kind: 'text', value });
const code = (value: string): ExportCell => ({ kind: 'label', value, code: value });
const date = (value: string | null | undefined): ExportCell => (value === null || value === undefined ? notReported : { kind: 'date', iso: value });

/** The negative on-hand beside the counted figure (< 0); blank where there is none. */
function negative(entry: OnHandEntry): ExportCell {
  const value = negativeOnHand(entry);
  return value === null ? notApplicable : { kind: 'integer', raw: Math.round(value) };
}

/** A stock figure: the number, «no row» for a pair with none on a landed day, else not reported. */
function stock(row: Pick<LookupRowModel, 'stockState'>, value: number | null): ExportCell {
  if (value !== null) return { kind: 'integer', raw: Math.round(value) };
  return row.stockState === 'no_row' ? notApplicable : notReported;
}

const sales = (value: number | null): ExportCell => (value === null ? notReported : { kind: 'integer', raw: value });

const STOCK_ABSENT = {
  notRanged: 'reason',
  reasonKeys: { 'not-ranged': 'strategic.health.lookup.absent.noRow', 'not-reported': 'strategic.health.lookup.absent.notReported' },
} as const;
const BLANK_WHERE_NOT_APPLICABLE = { notRanged: 'blank' } as const;
const PAIR_ONLY_ABSENT = {
  notRanged: 'blank',
  reasonKeys: { 'not-reported': 'strategic.health.lookup.absent.salesNotLanded' },
} as const;

/** The file's columns; the size columns only when the lookup was by size. */
export function lookupColumns(bySize: boolean): readonly ExportColumn[] {
  return [
    { key: 'country_key', headerKey: 'strategic.column.country', kind: 'text', descriptionKey: 'strategic.columnGuide.country', provenance: true },
    { key: 'as_of', headerKey: 'strategic.health.lookup.column.asOf', kind: 'date', descriptionKey: 'strategic.health.lookup.columnGuide.asOf', provenance: true },
    { key: 'store_key', headerKey: 'strategic.health.lookup.column.storeKey', kind: 'text', descriptionKey: 'strategic.health.lookup.columnGuide.storeKey', freeze: true, identifies: true },
    { key: 'store_name', headerKey: 'strategic.health.lookup.column.storeName', kind: 'text', descriptionKey: 'strategic.health.lookup.columnGuide.storeName' },
    { key: 'article_key', headerKey: 'strategic.health.lookup.column.articleKey', kind: 'text', descriptionKey: 'strategic.health.lookup.columnGuide.articleKey', freeze: true, identifies: true },
    { key: 'article_name', headerKey: 'strategic.health.lookup.column.articleName', kind: 'text', descriptionKey: 'strategic.health.lookup.columnGuide.articleName' },
    ...(bySize ? [
      { key: 'row_level', headerKey: 'strategic.health.lookup.column.rowLevel', kind: 'label', descriptionKey: 'strategic.health.lookup.columnGuide.rowLevel' },
      { key: 'size_code', headerKey: 'size.column.code', kind: 'text', descriptionKey: 'strategic.health.lookup.columnGuide.sizeCode', absent: BLANK_WHERE_NOT_APPLICABLE },
      { key: 'size_scale', headerKey: 'size.column.scale', kind: 'text', descriptionKey: 'strategic.health.lookup.columnGuide.sizeScale', absent: BLANK_WHERE_NOT_APPLICABLE, technical: true },
      { key: 'size_run_position', headerKey: 'size.column.runPosition', kind: 'integer', descriptionKey: 'strategic.health.lookup.columnGuide.sizeRunPosition', absent: BLANK_WHERE_NOT_APPLICABLE, technical: true },
    ] satisfies ExportColumn[] : []),
    { key: 'stock_state', headerKey: 'strategic.health.lookup.column.stockState', kind: 'label', descriptionKey: 'strategic.health.lookup.columnGuide.stockState' },
    { key: 'on_hand_units', headerKey: 'strategic.health.lookup.column.onHand', kind: 'integer', unit: 'units', descriptionKey: 'strategic.health.lookup.columnGuide.onHand', absent: STOCK_ABSENT },
    { key: 'negative_on_hand_units', headerKey: 'strategic.health.lookup.column.negativeOnHand', kind: 'integer', unit: 'units', descriptionKey: 'strategic.health.lookup.columnGuide.negativeOnHand', absent: BLANK_WHERE_NOT_APPLICABLE },
    { key: 'in_transit_units', headerKey: 'strategic.health.lookup.column.inTransit', kind: 'integer', unit: 'units', descriptionKey: 'strategic.health.lookup.columnGuide.inTransit', absent: STOCK_ABSENT },
    { key: 'sold_units', headerKey: 'strategic.health.lookup.column.sold', kind: 'integer', unit: 'units', descriptionKey: 'strategic.health.lookup.columnGuide.sold', absent: PAIR_ONLY_ABSENT },
    { key: 'returned_units', headerKey: 'strategic.health.lookup.column.returned', kind: 'integer', unit: 'units', descriptionKey: 'strategic.health.lookup.columnGuide.returned', absent: PAIR_ONLY_ABSENT },
    { key: 'net_value', headerKey: 'strategic.health.lookup.column.netValue', kind: 'number', descriptionKey: 'strategic.health.lookup.columnGuide.netValue', decimals: 2, absent: BLANK_WHERE_NOT_APPLICABLE },
    { key: 'sales_covered', headerKey: 'strategic.health.lookup.column.salesCovered', kind: 'label', descriptionKey: 'strategic.health.lookup.columnGuide.salesCovered' },
    { key: 'sales_from', headerKey: 'strategic.health.lookup.column.salesFrom', kind: 'date', descriptionKey: 'strategic.health.lookup.columnGuide.salesFrom', provenance: true },
    { key: 'sales_to', headerKey: 'strategic.health.lookup.column.salesTo', kind: 'date', descriptionKey: 'strategic.health.lookup.columnGuide.salesTo', provenance: true },
  ];
}

/** One pair as a row of the file, in `lookupColumns` order. Pure. */
export function lookupPairRow(answer: LookupResponseModel, row: LookupRowModel, bySize: boolean): ExportCell[] {
  return [
    text(answer.market), date(answer.asOf),
    text(row.storeKey), text(row.storeName), text(row.articleKey), text(row.articleName),
    ...(bySize ? [code('pair'), notApplicable, notApplicable, notApplicable] : []),
    code(row.stockState),
    stock(row, row.onHandUnits), negative(row), stock(row, row.inTransitUnits),
    sales(row.soldUnits), sales(row.returnedUnits),
    row.netValue === null ? notReported : { kind: 'number', raw: row.netValue },
    code(row.salesCovered ? 'covered' : 'not_covered'),
    date(answer.salesWindow.from), date(answer.salesWindow.to),
  ];
}

/** One size of a pair as a row of the file (by size only). Pure. */
export function lookupSizeRow(answer: LookupResponseModel, row: LookupRowModel, size: LookupSizeModel): ExportCell[] {
  return [
    text(answer.market), date(answer.asOf),
    text(row.storeKey), text(row.storeName), text(row.articleKey), text(row.articleName),
    code('size'), text(size.sizeCode), text(size.sizeScale),
    size.sizeRunPosition === null ? notReported : { kind: 'integer', raw: size.sizeRunPosition },
    code(row.stockState),
    stock(row, size.onHandUnits), negative(size), stock(row, size.inTransitUnits),
    sales(size.soldUnits), notApplicable, notApplicable,
    code(row.salesCovered ? 'covered' : 'not_covered'),
    date(answer.salesWindow.from), date(answer.salesWindow.to),
  ];
}

/** A pair's sizes in run order; a size with no position last, by its code. Pure. */
export function sizesInRunOrder(sizes: readonly LookupSizeModel[] | null | undefined): readonly LookupSizeModel[] {
  return [...(sizes ?? [])].sort((a, b) => {
    const pa = a.sizeRunPosition ?? Number.MAX_SAFE_INTEGER;
    const pb = b.sizeRunPosition ?? Number.MAX_SAFE_INTEGER;
    return pa !== pb ? pa - pb : (a.sizeCode ?? a.sizeKey).localeCompare(b.sizeCode ?? b.sizeKey);
  });
}

/** Every row of the file, pair by pair, each pair's sizes after it when by size. Pure. */
export function lookupRows(answer: LookupResponseModel | null, rows: readonly LookupRowModel[], bySize: boolean): readonly ExportCell[][] {
  if (answer === null) return [];
  return rows.flatMap((row) => [
    lookupPairRow(answer, row, bySize),
    ...(bySize ? sizesInRunOrder(row.sizes).map((size) => lookupSizeRow(answer, row, size)) : []),
  ]);
}

const LOOKUP_DEFINITION = 'Stock and sales of one store × article pair as landed in Optima (bata-platform POST /health/lookup): on hand and in transit are the stock day\'s rows summed over the pair\'s sizes — absent, never zero, when the pair has no row or the day has not landed; sold, returned and net value are the promoted, unsuperseded sales and returns batches over the window, a counted zero where the window landed with no sale.';

/**
 * The file of a lookup the page holds: the rows in the order the page draws them (`rows`, the table's
 * view), the answer for the scope. The page's own download and the route's export both end here.
 */
export function lookupRequest(answer: LookupResponseModel | null, rows: readonly LookupRowModel[], bySize: boolean): ExportRequest {
  const cells = lookupRows(answer, rows, bySize);
  const columns = lookupColumns(bySize);
  const filters: ProvenanceFilter[] = [];
  if (answer !== null) {
    filters.push(
      { field: 'country_key', operator: 'equals', value: answer.market },
      { field: 'as_of', operator: 'equals', value: answer.asOf },
      { field: 'sales_from', operator: 'gte', value: answer.salesWindow.from },
      { field: 'sales_to', operator: 'lte', value: answer.salesWindow.to },
      { field: 'pairs', operator: 'count', value: String(answer.rows.length) },
    );
  }
  return {
    id: LOOKUP_EXPORT_ID,
    titleKey: 'strategic.health.lookup.export.title',
    columns,
    rows: () => cells,
    rowCount: cells.length,
    provenance: {
      filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: answer === null ? NOT_PUBLISHED : { from: answer.salesWindow.from, to: answer.salesWindow.to },
      scenario: NOT_PUBLISHED,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{ measure: 'health.lookup', definition: LOOKUP_DEFINITION, registerVersion: '' }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: PLATFORM_VERSION,
    },
    perimeter: {
      filters,
      sort: [],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The route's export: the page's scope from the URL and the list this tab last looked up (the list is not
 * in the URL, `lookup.ts`), asked again. With no list or no market, the file has its columns and no row.
 */
export async function lookupExport(scope?: ExportScope): Promise<ExportRequest> {
  const parsed = readLookupScope(scope?.search ?? '');
  const body = lookupBody(parsed, parsed.asOf, parsePairs(storedPairs()).pairs);
  if (body === null) return lookupRequest(null, [], parsed.bySize);
  const answer = await callOperation(postLookup, { body });
  return lookupRequest(answer, answer.rows, parsed.bySize);
}
